import { Placement } from '@floating-ui/react-dom-interactions';
import React from 'react';
import styled, { css } from 'styled-components';
import { Colors, toRem } from '.';

export type PopArrowProps = {
    position: Placement,
    border?: string,
    color?: string,
    size?: number | string,
};

// Position is inverted because if the position is bottom, arrow points up...
const isTop = ({ position }: PopArrowProps) => position.includes('top');
const isBottom = ({ position }: PopArrowProps) => position.includes('bottom');
const isLeft = ({ position }: PopArrowProps) => position.includes('left');
const isRight = ({ position }: PopArrowProps) => position.includes('right');

const getColor = ({ color }: PopArrowProps) => color ?? Colors.primaryText;
const getSize = ({ size }: PopArrowProps) => toRem(size ?? 0.5);

export const PopArrow = styled.div<PopArrowProps>`
    position: absolute;
    left: 0;
    top: 0;
    width: 0; 
    height: 0;

    // Pointing Up
    ${p => isBottom(p) ? css`  
        left: 50%;
        margin-top: -${getSize(p)};
        border-left: ${getSize(p)} solid transparent;
        border-right: ${getSize(p)} solid transparent;
        border-bottom: ${getSize(p)} solid ${getColor(p)};
    ` : ''}
  
    // Pointing Down
    ${p => isTop(p) ? css`
        left: 50%;
        top: 100%;
        margin-bottom: -${getSize(p)};
        border-left: ${getSize(p)} solid transparent;
        border-right: ${getSize(p)} solid transparent;
        border-top: ${getSize(p)} solid ${getColor(p)};
    ` : ''}

    // Pointing Left
    ${p => isRight(p) ? css`  
        top: 50%;
        margin-left: -${getSize(p)};
        border-top: ${getSize(p)} solid transparent;
        border-bottom: ${getSize(p)} solid transparent; 
        border-right: ${getSize(p)} solid ${getColor(p)}; 
        filter: ${!!p.border ? `drop-shadow(-1px 0 0 ${p.border})` : 'none'};
    ` : ''}
  
    // Pointing Right
    ${p => isLeft(p) ? css`  
        left: 100%;
        top: 50%;
        margin-right: -${getSize(p)};
        border-top: ${getSize(p)} solid transparent;
        border-bottom: ${getSize(p)} solid transparent;
        border-left: ${getSize(p)} solid ${getColor(p)};
    ` : ''}

`;

const toRotation = (p: Placement) => {
    if (p.includes('left')) return 180;
    if (p.includes('right')) return 0;
    if (p.includes('bottom')) return 90;
    return 270;
};

const toOffset = (p: Placement) => {
    if (p.includes('left')) return [-2, 0];
    if (p.includes('right')) return [2, 0];
    if (p.includes('bottom')) return [0, 2];
    return [0, -2];
};

export type BetterPopArrowProps = {
    placement: Placement,
    border?: [number, string],
    color?: string,
    size?: number | string,
    style?: any,
};

export const BetterPopArrow = React.memo<BetterPopArrowProps>(({ placement, border, color, size, style }) => {
    const wh = toRem(size ?? 1);
    const [borderWidth, borderColor] = border ?? [];

    const rotation = toRotation(placement);
    const offset = !!borderWidth ? toOffset(placement) : [0, 0];

    style = {
        pointerEvents: 'none',
        position: 'absolute',
        transform: `translate(-50%, -50%) translate(${offset[0]}px, ${offset[1]}px)`,
        ...(style || {})
    };

    return <svg width={wh} height={wh} viewBox="0 0 16 16" style={style}>
        <g transform={`rotate(${rotation} 8 8)`}>
            <path d="M 0 8 L 8 0 L 8 16 Z" fill={borderColor ?? color} />
            {border && <path d={`M ${borderWidth} 8 L 8 ${borderWidth} L 8 ${16 - borderWidth} Z`} fill={color} />}
        </g>
    </svg>;
});
